import React, { useState, useEffect } from 'react';
import {
    Table,
    Tag,
    Typography,
    Alert,
    Button,
    Row,
    Col,
    Modal,
    message,
    Descriptions,
    Spin,
    Tabs,
    Tooltip as AntTooltip
} from 'antd';
import {
    CheckCircleOutlined,
    SafetyCertificateOutlined,
    ToolOutlined,
    CarOutlined,
    ArrowRightOutlined,
    ArrowLeftOutlined,
    EyeOutlined,
    WarningOutlined,
    DollarOutlined,
    PlusOutlined,
    HistoryOutlined
} from '@ant-design/icons';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    Cell,
    PieChart,
    Pie,
    AreaChart,
    Area
} from 'recharts';
import {
    TrendingUp,
    TrendingDown,
    Users,
    Fuel,
    LayoutDashboard,
    AlertTriangle,
    CheckCircle2
} from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';

const { Text, Title } = Typography;
const { TabPane } = Tabs;

// Premium Metric Card Component
const MetricCard = ({ title, value, subtext, icon, gradient, trend, trendValue, isRtl }) => (
    <div className={`relative overflow-hidden rounded-3xl p-6 shadow-lg border border-white/20 transition-all hover:scale-[1.02] hover:shadow-2xl h-full bg-gradient-to-br ${gradient} text-white`}>
        <div className="absolute top-0 right-0 -mr-8 -mt-8 w-32 h-32 bg-white/10 rounded-full blur-3xl"></div>
        <div className="relative z-10 flex justify-between items-start">
            <div className={`space-y-2 ${isRtl ? 'text-right' : 'text-left'}`}>
                <p className="text-white/70 text-[10px] font-bold uppercase tracking-widest">{title}</p>
                <div className={`flex items-baseline space-x-2 ${isRtl ? 'flex-row-reverse space-x-reverse' : ''}`}>
                    <h3 className="text-3xl font-black">{value}</h3>
                    {trend && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex items-center font-bold ${trend === 'up' ? 'bg-green-400/20 text-green-100' : 'bg-red-400/20 text-red-100'}`}>
                            {trend === 'up' ? <TrendingUp size={10} className={isRtl ? 'ml-1' : 'mr-1'} /> : <TrendingDown size={10} className={isRtl ? 'ml-1' : 'mr-1'} />}
                            {trendValue}
                        </span>
                    )}
                </div>
                <p className="text-white/60 text-[10px] font-medium leading-tight">{subtext}</p>
            </div>
            <div className="bg-white/10 p-3 rounded-2xl border border-white/20 backdrop-blur-md">
                {icon}
            </div>
        </div>
    </div>
);

// Quick Action Item
const QuickAction = ({ icon, label, onClick, color }) => (
    <button
        onClick={onClick}
        className="group flex flex-col items-center justify-center p-4 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-blue-100 transition-all space-y-2 min-w-[100px]"
    >
        <div className={`p-3 rounded-xl transition-colors ${color} group-hover:scale-110 transformation-all duration-300`}>
            {icon}
        </div>
        <span className="text-xs font-bold text-gray-600 group-hover:text-blue-600">{label}</span>
    </button>
);

const calculateAlerts = (vehicle, t) => {
    const alerts = [];
    const today = new Date();
    const daysBetween = (date) => Math.ceil((new Date(date) - today) / (1000 * 60 * 60 * 24));
    const kmRemaining = (target, current) => target - current;

    if (vehicle.date_expiration_assurance) {
        const days = daysBetween(vehicle.date_expiration_assurance);
        if (days <= 3) alerts.push({ type: 'insurance', message: t('insurance'), level: 'high', icon: <SafetyCertificateOutlined />, details: days <= 0 ? t('expired') : `${days}${t('remaining_days')}` });
        else if (days <= 7) alerts.push({ type: 'insurance', message: t('insurance'), level: 'medium', icon: <SafetyCertificateOutlined />, details: `${days}${t('remaining_days')}` });
    }

    if (vehicle.kilometrage_prochaine_vidange && vehicle.kilometrage) {
        const km = kmRemaining(vehicle.kilometrage_prochaine_vidange, vehicle.kilometrage);
        if (km <= 600) alerts.push({ type: 'maintenance', message: t('oil_change'), level: 'high', icon: <ToolOutlined />, details: km <= 0 ? t('late') : `${km}${t('remaining_km')}` });
        else if (km <= 1500) alerts.push({ type: 'maintenance', message: t('oil_change'), level: 'medium', icon: <ToolOutlined />, details: `${km}${t('remaining_km')}` });
    }

    if (vehicle.date_prochaine_visite_tech) {
        const days = daysBetween(vehicle.date_prochaine_visite_tech);
        if (days <= 3) alerts.push({ type: 'tech_control', message: t('tech_visit'), level: 'high', icon: <ToolOutlined />, details: days <= 0 ? t('overdue_short') : `${days}${t('remaining_days')}` });
        else if (days <= 7) alerts.push({ type: 'tech_control', message: t('tech_visit'), level: 'medium', icon: <ToolOutlined />, details: `${days}${t('remaining_days')}` });
    }

    return alerts;
};

const Dashboard = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const isRtl = i18n.language === 'ar';
    const [vehicles, setVehicles] = useState([]);
    const [reservations, setReservations] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState({
        totalVehicles: 0,
        available: 0,
        reserved: 0,
        highAlerts: 0,
        mediumAlerts: 0,
        dailyRevenue: 0,
        monthlyBalance: 0
    });

    const [selectedReservation, setSelectedReservation] = useState(null);
    const [detailsVisible, setDetailsVisible] = useState(false);
    const [vehicleDetails, setVehicleDetails] = useState(null);
    const [activeAlertTab, setActiveAlertTab] = useState('1');

    const fetchData = async () => {
        try {
            setLoading(true);
            const [vRes, rRes, tRes] = await Promise.all([
                axios.get('https://krili-loto.com/api/voitures'),
                axios.get('https://krili-loto.com/api/reservations?limit=1000'),
                axios.get('https://krili-loto.com/api/transactions?limit=1000') // Fetch more to calculate charts
            ]);

            const vehiclesWithAlerts = vRes.data.map(v => ({ ...v, alerts: calculateAlerts(v, t) }));
            setVehicles(vehiclesWithAlerts);
            setReservations(rRes.data.data || []);

            const txData = tRes.data.success ? tRes.data.data : [];
            setTransactions(txData);

            // Calculate Stats
            const highAlerts = vehiclesWithAlerts.filter(v => v.alerts.some(a => a.level === 'high')).length;
            const mediumAlerts = vehiclesWithAlerts.filter(v => v.alerts.some(a => a.level === 'medium')).length;

            const today = new Date().toDateString();
            const dailyRevenue = txData
                .filter(t => t.type === 'encaissement' && new Date(t.date).toDateString() === today)
                .reduce((sum, t) => sum + Number(t.amount), 0);

            const encaissements = txData.filter(t => t.type === 'encaissement').reduce((sum, t) => sum + Number(t.amount), 0);
            const decaissements = txData.filter(t => t.type === 'décaissement').reduce((sum, t) => sum + Number(t.amount), 0);

            setStats({
                totalVehicles: vRes.data.length,
                available: vRes.data.filter(v => v.disponible).length,
                reserved: vRes.data.filter(v => !v.disponible).length,
                highAlerts,
                mediumAlerts,
                dailyRevenue,
                monthlyBalance: encaissements - decaissements
            });

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [i18n.language]);

    // Chart Data Preparation
    const getRevenueChartData = () => {
        const last7Days = [...Array(7)].map((_, i) => {
            const d = new Date();
            d.setDate(d.getDate() - (6 - i));
            return d.toDateString();
        });

        return last7Days.map(dateStr => {
            const dayName = new Date(dateStr).toLocaleDateString(i18n.language === 'ar' ? 'ar-EG' : i18n.language === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'short' });
            const amount = transactions
                .filter(t => t.type === 'encaissement' && new Date(t.date).toDateString() === dateStr)
                .reduce((sum, t) => sum + Number(t.amount), 0);
            return { name: dayName, revenue: amount };
        });
    };

    const getStatusChartData = () => [
        { name: t('available'), value: stats.available, color: '#3b82f6' },
        { name: t('reserved'), value: stats.reserved, color: '#8b5cf6' }
    ];

    const todayRes = {
        departures: reservations.filter(r => new Date(r.date_depart).toDateString() === new Date().toDateString()),
        returns: reservations.filter(r => new Date(r.date_retour_prevue).toDateString() === new Date().toDateString()),
        overdue: reservations.filter(r => r.status === 'en_retard')
    };

    const handleShowDetails = async (reservation) => {
        setSelectedReservation(reservation);
        setDetailsVisible(true);
        try {
            const response = await axios.get(`https://krili-loto.com/api/voitures/${reservation.voiture_id}`);
            setVehicleDetails(response.data);
        } catch (err) {
            console.error(t('error_loading_details'));
        }
    };

    const reservationColumns = [
        { title: t('clients'), dataIndex: 'client_name', key: 'client_name', render: (text, r) => <span className="font-bold text-gray-700">{r.client_name_full || text}</span> },
        { title: t('vehicles'), render: (_, r) => <Tag className="rounded-md border-blue-100 text-blue-600 font-bold">{r.voiture_marque} {r.voiture_modele}</Tag> },
        {
            title: t('calendar'), render: (_, r) => (
                <div className={`flex items-center text-xs text-gray-500 font-medium ${isRtl ? 'flex-row-reverse' : ''}`}>
                    <span>{new Date(r.date_depart).toLocaleDateString()}</span>
                    <ArrowRightOutlined className={`mx-2 opacity-30 ${isRtl ? 'rotate-180' : ''}`} />
                    <span>{new Date(r.date_retour_prevue).toLocaleDateString()}</span>
                </div>
            )
        },
        { title: t('status'), render: (_, r) => <Tag className="rounded-full px-3 border-0 font-bold" color={r.status === 'en_retard' ? '#fee2e2' : r.status === 'en_cours' ? '#e0e7ff' : '#f0fdf4'} style={{ color: r.status === 'en_retard' ? '#991b1b' : r.status === 'en_cours' ? '#3730a3' : '#166534' }}>{t(r.status === 'en_retard' ? 'overdue' : r.status === 'en_cours' ? 'rental' : 'returns')}</Tag> },
        { title: t('action'), render: (_, r) => <Button type="text" icon={<EyeOutlined className="text-blue-600" />} onClick={() => handleShowDetails(r)} /> }
    ];

    return (
        <Sidebar>
            <div className={`min-h-screen bg-gray-50/50 p-4 lg:p-8 ${isRtl ? 'rtl' : ''}`}>
                {/* Dashboard Header */}
                <div className={`flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
                    <div className={isRtl ? 'text-right' : 'text-left'}>
                        <h2 className="text-3xl font-black text-gray-800 tracking-tight flex items-center">
                            <LayoutDashboard className={`${isRtl ? 'ml-3' : 'mr-3'} text-blue-600`} size={32} />
                            {t('dashboard')}
                        </h2>
                        <p className="text-gray-500 font-medium mt-1">{t('manage_fleet')}</p>
                    </div>
                    <div className={`flex space-x-3 ${isRtl ? 'space-x-reverse' : ''}`}>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            size="large"
                            className={`rounded-xl h-12 px-6 shadow-md shadow-blue-500/20 bg-blue-600 border-0 ${isRtl ? 'ml-3' : ''}`}
                            onClick={() => navigate('/reservations')}
                        >
                            {t('new_reservation')}
                        </Button>
                        <Button
                            icon={<HistoryOutlined />}
                            size="large"
                            className="rounded-xl h-12 px-6 bg-white border-gray-200"
                            onClick={() => fetchData()}
                        >
                            {t('refresh')}
                        </Button>
                    </div>
                </div>

                {/* Main Metrics Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" dir={isRtl ? 'rtl' : 'ltr'}>
                    <MetricCard
                        title={t('total_fleet')}
                        value={stats.totalVehicles}
                        subtext={`${stats.available} ${t('ready_to_use')}`}
                        icon={<CarOutlined style={{ fontSize: 24 }} />}
                        gradient="from-blue-600 to-indigo-700"
                        trend="up"
                        trendValue="+2"
                        isRtl={isRtl}
                    />
                    <MetricCard
                        title={t('active_alerts')}
                        value={stats.highAlerts + stats.mediumAlerts}
                        subtext={`${stats.highAlerts} ${t('need_attention')}`}
                        icon={<AlertTriangle size={24} />}
                        gradient="from-rose-500 to-orange-600"
                        trend={stats.highAlerts > 0 ? "up" : "down"}
                        trendValue={stats.highAlerts}
                        isRtl={isRtl}
                    />
                    <MetricCard
                        title={t('revenue_7d')}
                        value={`${stats.dailyRevenue} ${t('currency')}`}
                        subtext={t('received_today')}
                        icon={<DollarOutlined style={{ fontSize: 24 }} />}
                        gradient="from-emerald-500 to-teal-600"
                        trend="up"
                        trendValue="12%"
                        isRtl={isRtl}
                    />
                    <MetricCard
                        title={t('global_balance')}
                        value={`${stats.monthlyBalance} ${t('currency')}`}
                        subtext={t('balance_diff')}
                        icon={<TrendingUp size={24} />}
                        gradient="from-violet-500 to-fuchsia-600"
                        isRtl={isRtl}
                    />
                </div>

                {/* Sub Row: Quick Actions & Charts */}
                <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 mb-8" dir={isRtl ? 'rtl' : 'ltr'}>
                    {/* Revenue Trend Chart */}
                    <div className="xl:col-span-8 bg-white rounded-3xl p-6 shadow-sm border border-gray-100 flex flex-col">
                        <div className={`flex justify-between items-center mb-6 ${isRtl ? 'flex-row-reverse' : ''}`}>
                            <h3 className="text-lg font-bold text-gray-800 flex items-center">
                                <TrendingUp className={`${isRtl ? 'ml-2' : 'mr-2'} text-emerald-500`} size={20} />
                                {t('revenue_growth')}
                            </h3>
                            <div className={`flex bg-gray-50 rounded-lg p-1 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <button className="px-3 py-1 text-[10px] font-bold bg-white shadow-sm rounded-md text-gray-800 uppercase">{t('weekly_caps')}</button>
                                <button className="px-3 py-1 text-[10px] font-bold text-gray-400 uppercase">{t('monthly_caps')}</button>
                            </div>
                        </div>
                        <div className="h-[250px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={getRevenueChartData()} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 700, fill: '#64748b' }} dy={10} reversed={isRtl} />
                                    <YAxis hide orientation={isRtl ? 'right' : 'left'} />
                                    <RechartsTooltip
                                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', padding: '12px' }}
                                        labelStyle={{ fontWeight: 800, color: '#1e293b', marginBottom: '4px' }}
                                    />
                                    <Area type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Quick Actions & Distribution */}
                    <div className="xl:col-span-4 space-y-8">
                        {/* Quick Actions */}
                        <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100">
                            <h3 className={`text-sm font-black text-gray-400 uppercase tracking-widest mb-4 ${isRtl ? 'text-right' : ''}`}>{t('quick_actions')}</h3>
                            <div className={`flex flex-wrap gap-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <QuickAction icon={<Users size={20} />} label={t('clients')} color="bg-blue-50 text-blue-600" onClick={() => navigate('/clients')} />
                                <QuickAction icon={<CarOutlined />} label={t('vehicles')} color="bg-purple-50 text-purple-600" onClick={() => navigate('/voitures')} />
                                <QuickAction icon={<DollarOutlined />} label={t('finance')} color="bg-emerald-50 text-emerald-600" onClick={() => navigate('/finance')} />
                                <QuickAction icon={<ToolOutlined />} label={t('maintenance')} color="bg-orange-50 text-orange-600" onClick={() => navigate('/entretien')} />
                            </div>
                        </div>

                        {/* Availability Pie */}
                        <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 flex-1">
                            <h3 className={`text-sm font-black text-gray-400 uppercase tracking-widest mb-4 ${isRtl ? 'text-right' : ''}`}>{t('fleet_status')}</h3>
                            <div className="h-[180px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={getStatusChartData()} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                                            {getStatusChartData().map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <RechartsTooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className={`flex justify-center space-x-6 mt-2 ${isRtl ? 'space-x-reverse' : ''}`}>
                                {getStatusChartData().map(s => (
                                    <div key={s.name} className={`flex items-center space-x-2 ${isRtl ? 'space-x-reverse' : ''}`}>
                                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }}></div>
                                        <span className="text-[10px] font-bold text-gray-500 uppercase">{s.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Operations Section: Alerts & Reservations */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" dir={isRtl ? 'rtl' : 'ltr'}>
                    {/* Alerts Table */}
                    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className={`p-6 border-b border-gray-50 flex justify-between items-center bg-gray-50/50 ${isRtl ? 'flex-row-reverse' : ''}`}>
                            <h3 className="text-lg font-bold text-gray-800 flex items-center">
                                <AlertTriangle className={`${isRtl ? 'ml-2' : 'mr-2'} text-rose-500`} size={20} />
                                {t('maintenance_report')}
                            </h3>
                            <div className={`flex space-x-2 ${isRtl ? 'space-x-reverse' : ''}`}>
                                <button onClick={() => setActiveAlertTab('1')} className={`px-3 py-1 rounded-lg text-[10px] font-black tracking-tighter transition-all ${activeAlertTab === '1' ? 'bg-rose-100 text-rose-600 shadow-sm' : 'text-gray-400 hover:text-gray-600 uppercase'}`}>{t('urgent')}</button>
                                <button onClick={() => setActiveAlertTab('2')} className={`px-3 py-1 rounded-lg text-[10px] font-black tracking-tighter transition-all ${activeAlertTab === '2' ? 'bg-orange-100 text-orange-600 shadow-sm' : 'text-gray-400 hover:text-gray-600 uppercase'}`}>{t('medium')}</button>
                            </div>
                        </div>
                        <div className="p-0">
                            <Table
                                dataSource={vehicles.filter(v => v.alerts.some(a => a.level === (activeAlertTab === '1' ? 'high' : 'medium'))).slice(0, 5)}
                                columns={[
                                    { title: t('vehicles'), render: (_, v) => <div className={`leading-tight ${isRtl ? 'text-right' : 'text-left'}`}><p className="text-sm font-bold text-gray-800">{v.marque} {v.modele}</p><p className="text-[10px] text-gray-400 font-medium tracking-widest uppercase">{v.matricule}</p></div> },
                                    {
                                        title: t('maintenance'), render: (_, v) => v.alerts.filter(a => a.level === (activeAlertTab === '1' ? 'high' : 'medium')).map((a, i) => (
                                            <div key={i} className={`flex items-center py-1 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                                <div className={`w-1.5 h-1.5 rounded-full ${isRtl ? 'ml-2' : 'mr-2'} ${a.level === 'high' ? 'bg-rose-500' : 'bg-orange-500'}`}></div>
                                                <span className="text-xs font-semibold text-gray-600">{a.message}: <span className="font-bold text-gray-800">{a.details}</span></span>
                                            </div>
                                        ))
                                    }
                                ]}
                                pagination={false}
                                rowKey="id"
                                size="small"
                                className="dashboard-table"
                            />
                        </div>
                    </div>

                    {/* Reservations Tabs */}
                    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="bg-gray-50/50 border-b border-gray-50">
                            <Tabs defaultActiveKey="1" className={`px-6 reservation-tabs ${isRtl ? 'rtl-tabs' : ''}`} items={[
                                {
                                    key: '1',
                                    label: <span className={`flex items-center py-2 ${isRtl ? 'flex-row-reverse' : ''}`}><WarningOutlined className={`${isRtl ? 'ml-2' : 'mr-2'} text-rose-500`} />{t('overdue')} <span className={`${isRtl ? 'mr-2' : 'ml-2'} bg-rose-100 text-rose-600 px-1.5 rounded-md text-[10px]`}>{todayRes.overdue.length}</span></span>,
                                    children: <Table dataSource={todayRes.overdue} columns={reservationColumns} size="small" pagination={false} rowKey="id" scroll={{ x: 'max-content' }} />
                                },
                                {
                                    key: '2',
                                    label: <span className={`flex items-center py-2 ${isRtl ? 'flex-row-reverse' : ''}`}><ArrowRightOutlined className={`${isRtl ? 'ml-2 rotate-180' : 'mr-2'} text-blue-500`} />{t('departures')} <span className={`${isRtl ? 'mr-2' : 'ml-2'} bg-blue-100 text-blue-600 px-1.5 rounded-md text-[10px]`}>{todayRes.departures.length}</span></span>,
                                    children: <Table dataSource={todayRes.departures} columns={reservationColumns} size="small" pagination={false} rowKey="id" scroll={{ x: 'max-content' }} />
                                },
                                {
                                    key: '3',
                                    label: <span className={`flex items-center py-2 ${isRtl ? 'flex-row-reverse' : ''}`}><CheckCircle2 className={`${isRtl ? 'ml-2' : 'mr-2'} text-emerald-500`} size={14} />{t('returns')} <span className={`${isRtl ? 'mr-2' : 'ml-2'} bg-emerald-100 text-emerald-600 px-1.5 rounded-md text-[10px]`}>{todayRes.returns.length}</span></span>,
                                    children: <Table dataSource={todayRes.returns} columns={reservationColumns} size="small" pagination={false} rowKey="id" scroll={{ x: 'max-content' }} />
                                }
                            ]} />
                        </div>
                    </div>
                </div>

                {/* Reservation Detail Modal */}
                <Modal
                    title={<span className={`text-xl font-black text-gray-800 ${isRtl ? 'w-full text-right block' : ''}`}>{t('details')}</span>}
                    visible={detailsVisible}
                    onCancel={() => setDetailsVisible(false)}
                    footer={[
                        <Button key="close" onClick={() => setDetailsVisible(false)} className={`rounded-xl font-bold h-10 px-6 ${isRtl ? 'ml-2' : ''}`}>{t('close')}</Button>
                    ]}
                    centered
                    width={700}
                    className="premium-modal"
                >
                    {selectedReservation && (
                        <div className={`space-y-6 ${isRtl ? 'rtl' : ''}`}>
                            <div className={`bg-blue-50 p-6 rounded-3xl border border-blue-100 flex justify-between items-center ${isRtl ? 'flex-row-reverse text-right' : ''}`}>
                                <div>
                                    <p className="text-[10px] font-black text-blue-800 uppercase tracking-widest mb-1">{t('client_dossier')}</p>
                                    <h4 className="text-2xl font-bold text-gray-900">{selectedReservation.client_name_full || selectedReservation.client_name}</h4>
                                </div>
                                <div className="bg-white p-3 rounded-2xl shadow-sm text-center min-w-[100px]">
                                    <p className="text-[10px] font-bold text-gray-400 uppercase">{t('status')}</p>
                                    <Tag className="border-0 bg-transparent text-blue-600 font-black p-0 m-0 uppercase">{t(selectedReservation.status === 'en_retard' ? 'overdue' : selectedReservation.status === 'en_cours' ? 'rental' : 'returns')}</Tag>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className={`bg-gray-50 p-4 rounded-2xl border border-gray-100 ${isRtl ? 'text-right' : ''}`}>
                                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">{t('assigned_vehicle')}</p>
                                    <p className="text-lg font-bold text-gray-800">#{selectedReservation.voiture_marque} {selectedReservation.voiture_modele}</p>
                                    <p className="text-xs text-gray-500">{t('km_departure')}: {selectedReservation.km_depart} km</p>
                                </div>
                                <div className={`bg-gray-50 p-4 rounded-2xl border border-gray-100 ${isRtl ? 'text-right' : ''}`}>
                                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">{t('planning')}</p>
                                    <div className="space-y-1">
                                        <p className="text-xs font-bold text-gray-700">{t('departure')}: {new Date(selectedReservation.date_depart).toLocaleString()}</p>
                                        <p className="text-xs font-bold text-gray-700">{t('return')}: {new Date(selectedReservation.date_retour_prevue).toLocaleString()}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </Modal>
            </div>

            {/* Custom Styles for Ant Components to match Premium Look */}
            <style>{`
                .reservation-tabs .ant-tabs-nav::before { border-bottom: none !important; }
                .reservation-tabs .ant-tabs-tab { margin-right: 32px !important; }
                .reservation-tabs .ant-tabs-tab-active .ant-tabs-tab-btn { color: #1e293b !important; font-weight: 900 !important; }
                .reservation-tabs .ant-tabs-ink-bar { background: #3b82f6 !important; height: 3px !important; border-radius: 3px; }
                .dashboard-table .ant-table { background: transparent !important; }
                .dashboard-table .ant-table-thead > tr > th { background: transparent !important; border-bottom: 1px solid #f8fafc !important; font-size: 10px !important; font-weight: 800 !important; color: #94a3b8 !important; text-transform: uppercase; letter-spacing: 0.05em; text-align: ${isRtl ? 'right' : 'left'} !important; }
                .dashboard-table .ant-table-tbody > tr > td { border-bottom: 1px solid #f8fafc !important; padding: 12px 16px !important; text-align: ${isRtl ? 'right' : 'left'} !important; }
                .dashboard-table .ant-table-tbody > tr:hover > td { background: #f8fafc !important; }
                .rtl-tabs .ant-tabs-tab { margin-left: 32px !important; margin-right: 0 !important; }
                .rtl-tabs .ant-tabs-ink-bar { left: auto !important; right: auto !important; }
            `}</style>
        </Sidebar>
    );
};

export default Dashboard;
